import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, FolderOpen, Truck, Hammer, Wrench, Loader2, Trash2 } from "lucide-react";
import { useVanDesign } from "./VanDesignContext";
import { vanDesignApi } from "@/api/vanDesign";
import { VanHeader, NCard, NBtn, MonoBadge } from "./vanUI";

const CTAS = [
  { key: "intake", label: "Start New Design", Icon: Plus, variant: "gold", desc: "Photos → goals → AI draft" },
  { key: "load", label: "Load Saved Design", Icon: FolderOpen, variant: "ghost", desc: "Resume a build" },
  { key: "templates", label: "Browse Van Templates", Icon: Truck, variant: "ghost", desc: "Sprinter, Transit, ProMaster…" },
  { key: "diy", label: "DIY Blueprint", Icon: Hammer, variant: "ghost", desc: "Flat-pack assembly pack" },
  { key: "pro", label: "Pro Fabrication Pack", Icon: Wrench, variant: "ghost", desc: "Cabinet-maker spec sheet" },
];

export default function VanDesignHome({ onBack }) {
  const { reset, load, setScreen } = useVanDesign();
  const [projects, setProjects] = useState(null);

  useEffect(() => { vanDesignApi.listProjects().then(setProjects).catch(() => setProjects([])); }, []);

  const go = (key) => {
    if (key === "intake") { reset(); setScreen("intake"); return; }
    if (key === "templates") { reset(); setScreen("intake"); return; }
    if (key === "load") { document.getElementById("van-saved")?.scrollIntoView({ behavior: "smooth" }); return; }
    if (key === "diy" || key === "pro") {
      if (!projects?.length) { toast("Start or load a design first"); return; }
      openProject(projects[0], key === "diy" ? "blueprint" : "blueprint");
    }
  };

  const openProject = async (p, target = "editor") => {
    try {
      const full = await vanDesignApi.getProject(p.id);
      load(full); setScreen(target);
    } catch { toast.error("Could not open project"); }
  };

  const del = async (e, id) => {
    e.stopPropagation();
    try { await vanDesignApi.deleteProject(id); setProjects((ps) => ps.filter((x) => x.id !== id)); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#FDFBF7] max-w-[412px] mx-auto" data-testid="van-home">
      <VanHeader title="Van Life" sub="AI Layouts · Blueprints · 3D" onBack={onBack} testid="van-home-header" />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
        <NCard className="p-4">
          <p className="font-serif text-lg text-black leading-snug">Design modular van add-ons your way.</p>
          <p className="font-mono text-[11px] text-black/70 leading-relaxed mt-1.5">
            Upload a few photos, tell MiSt how you want to live, and pick a budget tier. Get an instant layout,
            a builder fabrication pack, a customer flat-pack guide, and a 3D preview.
          </p>
        </NCard>

        <div className="space-y-2.5">
          {CTAS.map(({ key, label, Icon, variant, desc }) => (
            <NBtn key={key} variant={variant} onClick={() => go(key)} testid={`van-cta-${key}`} className="justify-start px-4 py-3.5">
              <Icon size={16} /> <span className="flex-1 text-left">{label}</span>
              <span className="font-mono text-[8px] normal-case tracking-normal opacity-70 hidden xs:inline">{desc}</span>
            </NBtn>
          ))}
        </div>

        <div id="van-saved">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-2 mt-2">Recent Projects</p>
          {projects === null ? (
            <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-black" /></div>
          ) : projects.length === 0 ? (
            <NCard className="p-4"><p className="font-mono text-[11px] text-black/60">No saved builds yet — start a new design to see it here.</p></NCard>
          ) : (
            <div className="space-y-2">
              {projects.map((p) => (
                <NCard key={p.id} className="p-3 flex items-center gap-3 active:translate-x-[2px] active:translate-y-[2px]"
                  onClick={() => openProject(p)} data-testid={`van-project-${p.id}`}>
                  <span className="w-9 h-9 rounded-lg bg-[#D4AF37] border-2 border-black grid place-items-center shrink-0"><Truck size={16} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base text-black truncate">{p.name}</p>
                    <div className="flex gap-1.5 mt-0.5">
                      <MonoBadge>{(p.van?.model) || "custom"}</MonoBadge>
                      <MonoBadge tone="gold">{(p.goals?.budget_tier || "mid").toUpperCase()}</MonoBadge>
                      <MonoBadge>{(p.modules?.length || 0)} mods</MonoBadge>
                    </div>
                  </div>
                  <button onClick={(e) => del(e, p.id)} data-testid={`van-project-del-${p.id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-black bg-white"><Trash2 size={13} /></button>
                </NCard>
              ))}
            </div>
          )}
        </div>
        <div className="h-4" />
      </div>
    </div>
  );
}
